
import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link as LinkIcon, ExternalLink } from "lucide-react";
import { ElementVersion, ReferenceList } from "@/entities/all";
import ElementVersionForm from "./ElementVersionForm";
import ProcessingRulesPipeline from "./ProcessingRulesPipeline";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function ElementVersionModal({ isOpen, onClose, version, element, onVersionSaved, initialTab = "basic", readOnly = false, externalUrl = null }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referenceLists, setReferenceLists] = useState([]);
  const [latestVersion, setLatestVersion] = useState(null);
  const [savedVersion, setSavedVersion] = useState(null);

  const pipelineRef = useRef(null);

  // Fetch latest version when not in edit mode
  useEffect(() => {
    async function fetchLatestVersion() {
      if (isOpen && !version && element?.id) {
        try {
          const versions = await ElementVersion.filter({ element_id: element.id }, '-version_number', 1);
          if (versions.length > 0) {
            setLatestVersion(versions[0]);
          } else {
            setLatestVersion(null);
          }
        } catch (error) {
          console.error("Failed to fetch latest version:", error);
          setLatestVersion(null);
        }
      }
    }
    fetchLatestVersion();
  }, [isOpen, version, element?.id]);


  const getInitialFormData = () => ({
    definition: '',
    data_type: 'string',
    test_cases: [],
    change_description: '',
    other_attributes: {},
    status: 'DRAFT',
    reference_list_id: null,
    confidence_score: 0.95,
    creation_method: 'MANUAL',
    creation_details: {},
    completeness_weight: 1,
    timeliness_weight: 1
  });

  useEffect(() => {
    async function fetchLists() {
      try {
        const lists = await ReferenceList.list();
        setReferenceLists(lists);
      } catch (error) {
        console.error("Failed to fetch reference lists:", error);
      }
    }
    fetchLists();
  }, []);

  // The effective version: use savedVersion (after create) or the passed-in version prop
  const effectiveVersion = savedVersion || version;

  useEffect(() => {
    if (!isOpen) {
      setSavedVersion(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (effectiveVersion) {
        setFormData({
          definition: effectiveVersion.definition || '',
          data_type: effectiveVersion.data_type || 'string',
          test_cases: effectiveVersion.test_cases || [],
          change_description: effectiveVersion.change_description || '',
          other_attributes: effectiveVersion.other_attributes || {},
          status: effectiveVersion.status || 'DRAFT',
          reference_list_id: effectiveVersion.reference_list_id || null,
          confidence_score: effectiveVersion.confidence_score || 0.95,
          creation_method: effectiveVersion.creation_method || 'MANUAL',
          creation_details: effectiveVersion.creation_details || {},
          completeness_weight: effectiveVersion.completeness_weight ?? 1,
          timeliness_weight: effectiveVersion.timeliness_weight ?? 1
        });
      } else {
         // Reset for new version, prepopulating with latest version if available
         const sourceData = latestVersion || {};

         // Only prepopulate version-specific fields, exclude element-level fields
         const versionFields = {
           definition: sourceData.definition || '',
           data_type: sourceData.data_type || 'string',
           test_cases: sourceData.test_cases || [],
           other_attributes: sourceData.other_attributes || {},
           reference_list_id: sourceData.reference_list_id || null,
           confidence_score: sourceData.confidence_score || 0.95,
           creation_method: sourceData.creation_method || 'MANUAL',
           creation_details: sourceData.creation_details || {},
           completeness_weight: sourceData.completeness_weight ?? 1,
           timeliness_weight: sourceData.timeliness_weight ?? 1
         };

        setFormData({
           ...getInitialFormData(),
           ...versionFields,
           status: 'DRAFT' // Always DRAFT for new version
        });
      }
    }
  }, [effectiveVersion, element, isOpen, latestVersion]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      let payload = { ...formData };
      
      // Ensure JSON fields are objects
      try {
        if (typeof payload.other_attributes === 'string' && payload.other_attributes.trim() !== '') {
          payload.other_attributes = JSON.parse(payload.other_attributes);
        } else if (payload.other_attributes === '' || payload.other_attributes === null) {
          payload.other_attributes = {}; // Set to empty object if empty string/null
        }
      } catch (error) {
        alert('Validation Error: "Other Attributes" is not valid JSON. Please correct it.');
        console.error('Invalid JSON for other_attributes:', error);
        setIsSubmitting(false);
        return;
      }
      try {
        if (typeof payload.creation_details === 'string' && payload.creation_details.trim() !== '') {
          payload.creation_details = JSON.parse(payload.creation_details);
        } else if (payload.creation_details === '' || payload.creation_details === null) {
          payload.creation_details = {}; // Set to empty object if empty string/null
        }
      } catch (error) {
        alert('Validation Error: "Creation Details" is not valid JSON. Please correct it.');
        console.error('Invalid JSON for creation_details:', error);
        setIsSubmitting(false);
        return;
      }

      let savedVersionId = effectiveVersion?.id;
      const userName = (user?.first_name && user?.last_name)
        ? `${user.first_name} ${user.last_name}`
        : user?.email || 'Unknown User';

      if (effectiveVersion) {
        // Update existing version (only if DRAFT)
        await ElementVersion.update(effectiveVersion.id, { ...payload, changed_by: userName });
      } else {
        // Create new version
        const existingVersions = await ElementVersion.filter({ element_id: element.id });
        const nextVersionNumber = Math.max(0, ...existingVersions.map(v => v.version_number || 0)) + 1;
        const newVersion = await ElementVersion.create({
          ...payload,
          element_id: element.id,
          version_number: nextVersionNumber,
          changed_by: userName
        });
        savedVersionId = newVersion.id;
        // Switch to edit mode so subsequent saves update instead of creating again
        setSavedVersion({ ...payload, id: newVersion.id, element_id: element.id, version_number: nextVersionNumber });
      }

      // Save pipeline changes if there are any
      let pipelineFailed = false;
      if (pipelineRef.current && pipelineRef.current.hasPendingChanges()) {
        const pipelineResult = await pipelineRef.current.savePipeline(savedVersionId);
        if (!pipelineResult.success) {
          pipelineFailed = true;
          toast.error('Version saved but failed to save processing rules: ' + pipelineResult.error);
        }
      }

      if (!pipelineFailed) {
        toast.success(isEditMode ? 'Version updated' : 'Version created');
      }
      if (onVersionSaved) {
        onVersionSaved();
      }
    } catch (error) {
      console.error('Error saving version:', error);
      alert('Error saving version: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isEditMode = !!effectiveVersion;
  const canEdit = !readOnly && (!effectiveVersion || effectiveVersion.status === 'DRAFT');

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="!max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle>
                {isEditMode ? `Edit Version ${effectiveVersion.version_number}` : `Create New Version for ${element?.canonical_name || ''}`}
              </DialogTitle>
              {externalUrl && (
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => window.open(externalUrl, '_blank')}>
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open Element
                </Button>
              )}
            </div>
            <DialogDescription>
              {isEditMode
                ? `Modify the details for version ${effectiveVersion.version_number} of ${element?.canonical_name || ''}`
                : `Create a new version for ${element?.canonical_name || ''}`
              }
            </DialogDescription>
            {isEditMode && !canEdit && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 w-fit">
                Read-only: Only DRAFT versions can be edited
              </Badge>
            )}
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <Tabs defaultValue={initialTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="processing">Processing</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4 pt-4">
                <ElementVersionForm
                  formData={formData}
                  onChange={handleChange}
                  disabled={!canEdit}
                  referenceLists={referenceLists}
                />
              </TabsContent>

              <TabsContent value="processing" className="space-y-6 pt-4">
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                  <div className="flex items-center gap-2 mb-3">
                    <LinkIcon className="w-4 h-4 text-blue-600" />
                    <Label className="font-semibold">Processing Rules Pipeline</Label>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    Link reusable processing rules from the repository. Rules execute in sequence.
                  </p>
                  <ProcessingRulesPipeline
                    ref={pipelineRef}
                    elementVersionId={effectiveVersion?.id || latestVersion?.id}
                    disabled={!canEdit}
                  />
                </div>
              </TabsContent>

            </Tabs>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {canEdit && (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting 
                    ? 'Saving...' 
                    : isEditMode 
                      ? 'Update Version' 
                      : 'Create Version'
                  }
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
